import { Box, Skeleton, Stack } from "@mui/material";
import { Tab, Tabs, Text } from "@src/appV2/redesign/system/ui-components";
import { Fragment, useState } from "react";

import { type QualificationRatios, usePatientLoadData } from "./hooks/usePatientLoadData";

interface PatientLoadSectionProps {
  workplaceId: string;
}

interface PeriodColumnProps {
  label: string;
  value?: string;
}

function PeriodColumn(props: PeriodColumnProps) {
  const { label, value } = props;

  return (
    <Stack alignItems="center" spacing={1} sx={{ flex: 1, paddingY: 2 }}>
      <Text semibold variant="h5" color={(theme) => theme.palette.text.primary}>
        {value ?? "N/A"}
      </Text>
      <Text variant="body2" color={(theme) => theme.palette.text.secondary}>
        {label}
      </Text>
    </Stack>
  );
}

interface PatientLoadGridProps {
  ratios: QualificationRatios;
}

const PERIOD_COLUMNS = [
  { key: "am", label: "Day" },
  { key: "pm", label: "Evening" },
  { key: "noc", label: "Overnight" },
] as const;

function PatientLoadGrid(props: PatientLoadGridProps) {
  const { ratios } = props;

  return (
    <Stack direction="row" alignItems="center">
      {PERIOD_COLUMNS.map((column, index) => (
        <Fragment key={column.key}>
          <PeriodColumn label={column.label} value={ratios[column.key]} />
          {index < PERIOD_COLUMNS.length - 1 && (
            <Box
              sx={(theme) => ({
                width: "1px",
                alignSelf: "stretch",
                marginY: 2,
                backgroundColor: theme.palette.border?.primary,
              })}
            />
          )}
        </Fragment>
      ))}
    </Stack>
  );
}

function PatientLoadSkeleton() {
  return (
    <Stack spacing={5}>
      <Skeleton variant="rectangular" height={40} width="60%" />
      <Stack direction="row" spacing={3}>
        <Skeleton variant="rectangular" height={60} sx={{ flex: 1 }} />
        <Skeleton variant="rectangular" height={60} sx={{ flex: 1 }} />
        <Skeleton variant="rectangular" height={60} sx={{ flex: 1 }} />
      </Stack>
    </Stack>
  );
}

export function PatientLoadSection(props: PatientLoadSectionProps) {
  const { workplaceId } = props;
  const { qualificationRatios, isLoading } = usePatientLoadData({ workplaceId });
  const [selectedQualification, setSelectedQualification] = useState<string | undefined>(undefined);

  // Don't render if no data is available
  if (!isLoading && qualificationRatios.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <Stack spacing={5} sx={{ paddingTop: 8 }}>
        <Text semibold variant="body1">
          Patient Load
        </Text>
        <PatientLoadSkeleton />
      </Stack>
    );
  }

  const activeQualification = selectedQualification ?? qualificationRatios[0]?.qualification;
  const activeRatios = qualificationRatios.find(
    (ratios) => ratios.qualification === activeQualification
  );

  if (!activeRatios) {
    return null;
  }

  return (
    <Stack spacing={5} sx={{ paddingTop: 8 }}>
      <Text semibold variant="body1">
        Patient Load
      </Text>

      <Box sx={{ alignSelf: "flex-start" }}>
        <Tabs
          variant="pill"
          value={activeQualification}
          onChange={(_, newValue: string) => {
            setSelectedQualification(newValue);
          }}
        >
          {qualificationRatios.map((ratios) => (
            <Tab
              key={ratios.qualification}
              variant="pill"
              label={ratios.qualification}
              value={ratios.qualification}
            />
          ))}
        </Tabs>
      </Box>

      <PatientLoadGrid ratios={activeRatios} />
    </Stack>
  );
}
